import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  ValetOrderStandaloneDocument,
  adaptScenarioOrderStandalone,
  buildValetStandaloneVariables,
  isOrderAssignmentSettled,
} from '@hopdrive/feature-order-index';
import type {
  ScenarioAssignmentAction,
  ValetOrderStandaloneQuery,
  ValetOrderStandaloneQueryVariables,
} from '@hopdrive/feature-order-index';

const SETTLE_POLL_INTERVAL_MS = 1_000;
const SETTLE_POLL_LIMIT = 30;

/**
 * The window between an assign-modal success and the data agreeing with it.
 *
 * The dispatch path is asynchronous AND does not reliably bump `updatedat`, so
 * nothing streams when the driver lands — the order has to be asked. This polls the
 * standalone order query for ONE scenario (the whole shell; no per-move bookkeeping)
 * at `network-only`, which writes the same normalized entities every card and row
 * render from: the pill corrects itself the moment a poll brings the driver, and the
 * settle check only decides when the assigning state comes off. Bounded like the
 * legacy dashboard's poll — a dispatch that never lands stops spinning after the
 * limit rather than forever.
 */
export const useOrderAssignmentSettling = (): {
  assigningScenarioIds: ReadonlySet<number>;
  beginSettling: (input: { scenarioId: number; action: ScenarioAssignmentAction; driverId: number | null }) => void;
} => {
  const client = useApolloClient();
  const [assigningScenarioIds, setAssigningScenarioIds] = useState<ReadonlySet<number>>(new Set());
  const intervalsRef = useRef(new Map<number, ReturnType<typeof setInterval>>());

  const stopSettling = useCallback((scenarioId: number) => {
    const interval = intervalsRef.current.get(scenarioId);
    if (interval) clearInterval(interval);
    intervalsRef.current.delete(scenarioId);
    setAssigningScenarioIds(previous => {
      if (!previous.has(scenarioId)) return previous;
      const next = new Set(previous);
      next.delete(scenarioId);
      return next;
    });
  }, []);

  const beginSettling = useCallback(
    ({ scenarioId, action, driverId }: { scenarioId: number; action: ScenarioAssignmentAction; driverId: number | null }) => {
      // A second action on the same order supersedes the first outright — its poll
      // would otherwise clear the new action's state against the old verdict.
      stopSettling(scenarioId);
      setAssigningScenarioIds(previous => new Set(previous).add(scenarioId));

      let polls = 0;
      const interval = setInterval(() => {
        polls += 1;
        client
          .query<ValetOrderStandaloneQuery, ValetOrderStandaloneQueryVariables>({
            query: ValetOrderStandaloneDocument,
            variables: buildValetStandaloneVariables(scenarioId) as ValetOrderStandaloneQueryVariables,
            fetchPolicy: 'network-only',
          })
          .then(result => {
            const order = adaptScenarioOrderStandalone(result.data);
            const settled =
              !!order && isOrderAssignmentSettled({ legs: order.legs, offers: order.offers ?? [], action, driverId });
            if (settled || polls >= SETTLE_POLL_LIMIT) stopSettling(scenarioId);
          })
          .catch(() => {
            if (polls >= SETTLE_POLL_LIMIT) stopSettling(scenarioId);
          });
      }, SETTLE_POLL_INTERVAL_MS);
      intervalsRef.current.set(scenarioId, interval);
    },
    [client, stopSettling]
  );

  useEffect(() => {
    const intervals = intervalsRef.current;
    return () => intervals.forEach(interval => clearInterval(interval));
  }, []);

  return { assigningScenarioIds, beginSettling };
};
